import React, { useEffect, useState, useCallback } from "react";
import { PageHeader, Toast, Modal, EmptyState } from "@/components/Layout";
import api from "@/lib/api";
import ImportFileStep from "@/components/ImportFileStep";
import { useAuth } from "@/context/AuthContext";
import { lock } from "@/lib/perm";
import { postImport } from "@/lib/importJob";

import RowActions from "@/components/RowActions";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KINDS = [
  { key: "stat", label: "Funcții contractuale" },
  { key: "internal", label: "Funcții interne de execuție" },
  { key: "cor", label: "Coduri COR", code: true },
  { key: "salarizare", label: "Funcții Legea salarizării (execuție)" },
  { key: "management_internal", label: "Funcții interne de conducere" },
  { key: "management_salarizare", label: "Funcții Legea salarizării (conducere)" },
];

export default function Functions() {
  const { has } = useAuth();
  const [kind, setKind] = useState("stat");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [msg, setMsg] = useState({ text: "", type: "info" });
  const cur = KINDS.find(k => k.key === kind);

  const load = useCallback(async () => {
    const r = await api.get("/mdm/functions", { params: { kind, q, include_inactive: showInactive } });
    setRows(r.data);
  }, [kind, q, showInactive]);
  useEffect(() => { load(); }, [load]);

  const flash = (text, type = "success") => { setMsg({ text, type }); setTimeout(() => setMsg({ text: "" }), 2000); };

  const save = async () => {
    try {
      const body = { kind, name: editing.name, code: editing.code || "", active: editing.active !== false };
      if (editing.id) await api.put(`/mdm/functions/${editing.id}`, body);
      else await api.post("/mdm/functions", body);
      setEditing(null); load(); flash("Salvat");
    } catch (e) { flash(e?.response?.data?.detail || "Eroare", "error"); }
  };
  const deactivate = async (f) => {
    try { await api.delete(`/mdm/functions/${f.id}`); load(); flash("Dezactivat"); }
    catch (e) { flash(e?.response?.data?.detail || "Eroare", "error"); }
  };
  const reactivate = async (f) => {
    try { await api.put(`/mdm/functions/${f.id}`, { kind, name: f.name, code: f.code, active: true }); load(); flash("Reactivat"); }
    catch (e) { flash(e?.response?.data?.detail || "Eroare", "error"); }
  };
  const downloadTemplate = () => {
    fetch(`${API}/mdm/functions/import/template?kind=${kind}`, { credentials: "include" })
      .then(r => r.blob()).then(b => {
        const a = document.createElement("a"); a.href = URL.createObjectURL(b);
        a.download = `template_${kind}.xlsx`; a.click();
      });
  };
  // aruncă la eroare: mesajul exact al serverului îl afișează ImportFileStep sub butonul „Importă”
  const upload = async (file, onProgress) => {
    const r = await postImport(`/mdm/functions/import?kind=${kind}`, file, onProgress);
    setImportResult(r.data); load();
  };

  return (
    <>
      <PageHeader title="Funcții și coduri COR" subtitle="Nomenclatoare folosite la Rapoartele de muncă"
        actions={<>
          <button className="btn btn-secondary" onClick={downloadTemplate} data-testid="fn-template" {...lock(has("functions.import"))}>↓ Template Excel</button>
          <button className="btn btn-secondary" onClick={() => { setImportResult(null); setImporting(true); }} data-testid="fn-import" {...lock(has("functions.import"))}>Import Excel</button>
          <button className="btn btn-primary" onClick={() => setEditing({ name: "", code: "", active: true })} data-testid="fn-add" {...lock(has("functions.manage"))}>+ Adaugă</button>
        </>} />
      <Toast msg={msg.text} type={msg.type} />
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {KINDS.map(k => (
          <button key={k.key} className={`btn ${k.key === kind ? "btn-primary" : "btn-secondary"}`}
            onClick={() => { setKind(k.key); setQ(""); }} data-testid={`fn-tab-${k.key}`}>{k.label}</button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
        <input className="input" style={{ maxWidth: 360 }} placeholder={cur.code ? "Caută după denumire sau cod…" : "Caută după denumire…"}
          value={q} onChange={e => setQ(e.target.value)} data-testid="fn-search" />
        <label style={{ fontSize: 13 }}><input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} /> arată și dezactivate</label>
        <span style={{ fontSize: 12, color: "#64748B" }}>{rows.length} intrări</span>
      </div>
      <div className="card">
        <table className="tbl">
          <thead><tr><th>Denumire</th>{cur.code && <th>Cod COR</th>}<th>Status</th><th></th></tr></thead>
          <tbody>{rows.map(f => (
            <tr key={f.id}>
              <td>{f.name}</td>
              {cur.code && <td className="tabular-nums">{f.code}</td>}
              <td>{f.active ? "Activă" : "Dezactivată"}</td>
              <td style={{ textAlign: "right" }}>
                <RowActions testid={`row-menu-${f.name}`} items={[
                  { label: "Editează", icon: "edit", onClick: () => setEditing({ ...f }), ...lock(has("functions.manage")) },
                  f.active
                    ? { label: "Dezactivează", icon: "close", danger: true, btnDanger: true, onClick: () => deactivate(f), ...lock(has("functions.manage")) }
                    : { label: "Reactivează", icon: "check", onClick: () => reactivate(f), ...lock(has("functions.manage")) },
                ]} />
              </td>
            </tr>))}</tbody>
        </table>
        {rows.length === 0 && <EmptyState text='Lista e goală. Folosește „+ Adaugă” sau „Import Excel”.' />}
      </div>

      {editing && (
        <Modal title={editing.id ? "Editează" : `Adaugă — ${cur.label}`} onClose={() => setEditing(null)} width={480}>
          <div style={{ display: "grid", gap: 12 }}>
            <div><label className="label label-req">Denumire</label>
              <input className="input" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} data-testid="fn-name" /></div>
            {cur.code && <div><label className="label label-req">Cod COR (4–6 cifre)</label>
              <input className="input" value={editing.code || ""} onChange={e => setEditing({ ...editing, code: e.target.value })} data-testid="fn-code" /></div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setEditing(null)}>Renunță</button>
              <button className="btn btn-primary" onClick={save} data-testid="fn-save" {...lock(has("functions.manage"))}>Salvează</button>
            </div>
          </div>
        </Modal>
      )}

      {importing && (
        <Modal title={`Import Excel — ${cur.label}`} onClose={() => { setImporting(false); setImportResult(null); }} width={560}>
          {!importResult ? (
            <div style={{ fontSize: 13, color: "#334155" }}>
              <p style={{ marginTop: 0 }}>Fișierul are coloanele <b>Denumire</b>{cur.code && <> și <b>Cod COR</b></>}. Intrările existente se sar, cele noi se adaugă.</p>
              <ImportFileStep accept=".xlsx" onImport={upload} testId="fn-import-file" compact />
            </div>
          ) : (
            <div>
              <div style={{ padding: 14, background: "var(--tint-100)", borderRadius: 6, marginBottom: 12 }}>
                <div style={{ fontWeight: 700, color: "var(--navy-3)" }}>Import finalizat</div>
                <div style={{ fontSize: 13 }}>Adăugate: <b>{importResult.created}</b> · Sărite (existau): <b>{importResult.skipped}</b></div>
              </div>
              {importResult.errors.length > 0 && (
                <div style={{ maxHeight: 220, overflow: "auto", border: "1px solid #FCA5A5", borderRadius: 6 }}>
                  <table className="tbl" style={{ fontSize: 12 }}>
                    <thead><tr><th>Rând</th><th>Eroare</th></tr></thead>
                    <tbody>{importResult.errors.map((e, i) => <tr key={i}><td>{e.row}</td><td>{e.error}</td></tr>)}</tbody>
                  </table>
                </div>)}
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
